/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { FunctionComponent, useMemo } from 'react';
import { Reducer } from 'redux';
import { useSelector } from 'react-redux';
import { getIsDarkModeSelector } from 'common/redux/userSettings/userSettingsSlice';
import { LoaderProvider, LoaderThemeLight, PwcLoader } from '@pwc/pwc-deals-ui';
import { useHistory } from 'react-router-dom';
import { IEvent, useEventSubscription } from '@pwc/event-hub-ui';
import { Loader } from '../Loader';
import { useFederatedScript } from './useFederatedScript';
import {
  useFederatedReducer,
  useFederatedComponent,
} from './useFederatedModules';

import styles from './index.module.scss';

export enum LoaderType {
  Pwc,
  Circular,
}

type FederatedMFEProps = {
  urlKey: string;
  scope: string;
  reducerModule?: string;
  componentModule: string;
  suspenseFallbackStyle?: string;
  injectionReducerCb: (reducers: Reducer) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props?: any;
  loader?: LoaderType;
};

const FederatedMFE: FunctionComponent<FederatedMFEProps> = ({
  urlKey,
  scope,
  reducerModule,
  componentModule,
  injectionReducerCb,
  suspenseFallbackStyle,
  props,
  loader = LoaderType.Pwc,
}: FederatedMFEProps) => {
  const { ready, error: scriptError } = useFederatedScript(urlKey);
  const { injected, reducerError } = useFederatedReducer(
    ready,
    scope,
    reducerModule,
    injectionReducerCb
  );

  const history = useHistory();

  useEventSubscription(
    'route-update',
    (
      event: IEvent<{
        url: string;
      }>
    ) => {
      if (event.data?.url) {
        history.push(event.data?.url);
      }
    }
  );

  const darkMode = useSelector(getIsDarkModeSelector);
  const themeName = useMemo(() => {
    if (darkMode === undefined) return 'default';
    return darkMode ? 'default' : 'light';
  }, [darkMode]);

  const Component = useFederatedComponent(
    ready,
    injected,
    scope,
    componentModule
  ) as unknown as React.ExoticComponent<{
    children?: React.ReactNode;
    projectId?: number;
    disabledSections?: {
      files?: boolean;
    };
  }>;

  return (
    <LoaderProvider
      config={{
        errorCodesMapping: { mappings: {}, codes: {} },
        themeConfig: { userThemes: { light: LoaderThemeLight } },
      }}
    >
      {loader === LoaderType.Pwc && (
        <div className="main-wrapper">
          <PwcLoader
            loaderType="large"
            classes="large"
            loading={false}
            errorConfig={{
              type: 'newPage',
              id: '.main-wrapper',
              themeName,
              error:
                scriptError || reducerError
                  ? {
                      message: 'Unable to load MFE',
                    }
                  : undefined,
              props: {
                confirm: () => {
                  window.location.reload();
                },
                classes: styles['mfe-error-wrapper'],
              },
            }}
          />
        </div>
      )}
      <React.Suspense
        fallback={
          <div className={suspenseFallbackStyle}>
            {loader === LoaderType.Pwc && (
              <PwcLoader loaderType="large" loading />
            )}
            {loader === LoaderType.Circular && <Loader />}
          </div>
        }
      >
        {Component !== null && <Component {...props} />}
      </React.Suspense>
    </LoaderProvider>
  );
};

export default FederatedMFE;
